import { RAW_ANNOTATION_TAG_NAMES } from "@perceptron/core/types";

import { TOOL_CALL_SLOT_TAG } from "./tool-calls/ToolCallSlot";

/** Tags whose content may hold blank lines; a block never ends inside one. */
const CONTAINER_TAGS = [...RAW_ANNOTATION_TAG_NAMES, "think", TOOL_CALL_SLOT_TAG];
const CONTAINER_TAG = new RegExp(`<(\\/)?(${CONTAINER_TAGS.join("|")})(?=[\\s/>])([^>]*)>`, "g");
const FENCE_OPEN = /^\s{0,3}(`{3,}|~{3,})/;
/** A line that carries on the block above it across a blank line: a list item, an indented line, a quote. */
const CONTINUATION = /^(\s{2,}|\t|\s{0,3}([-*+]|\d+[.)])\s|\s{0,3}>)/;

/**
 * A link-reference definition (`[1]: https://…`) or a footnote definition (`[^1]: …`). Each resolves
 * across the whole document, so a document holding one cannot be parsed a block at a time.
 */
const REFERENCE_DEFINITION = /^ {0,3}\[[^\]\n]+\]:/m;

/**
 * Splits markdown into top-level blocks at blank lines, so a streaming render can re-parse only the block
 * still being written (`MarkdownBlocks`). A blank line only ends a block outside a code fence, outside an
 * open annotation or tool tag, and when the next line does not continue what came before (a list, an
 * indented continuation, a blockquote), so each block parses as it would inside the whole document. A
 * document with reference or footnote definitions is not split at all.
 */
export function splitMarkdownBlocks(text: string): string[] {
  // References and footnotes need their definitions in the same parse; keep such a document whole.
  if (REFERENCE_DEFINITION.test(text)) return [text];
  const blocks: string[] = [];
  const lines = text.split("\n");
  let current: string[] = [];
  let fence: string | null = null;
  let depth = 0;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!;
    if (fence !== null) {
      current.push(line);
      if (line.trimStart().startsWith(fence)) fence = null;
      continue;
    }
    const opening = FENCE_OPEN.exec(line);
    if (opening) {
      fence = opening[1]!;
      current.push(line);
      continue;
    }
    for (const match of line.matchAll(CONTAINER_TAG)) {
      if ((match[3] ?? "").trimEnd().endsWith("/")) continue;
      depth = Math.max(0, depth + (match[1] ? -1 : 1));
    }
    if (line.trim() === "" && depth === 0 && current.length > 0) {
      const next = lines.slice(i + 1).find((candidate) => candidate.trim() !== "");
      if (next === undefined || !CONTINUATION.test(next)) {
        blocks.push(current.join("\n"));
        current = [];
        continue;
      }
    }
    current.push(line);
  }
  if (current.length > 0) blocks.push(current.join("\n"));
  return blocks;
}
